import { Button } from "@coval/ui"
import { ChartFigure, type ChartTable } from "@coval/ui/chart-figure"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { logger } from "@/lib/logger"
import { DIVIDER_STROKE } from "@/components/charts/chartTheme"
import { lineSeriesColors } from "@/components/charts/seriesPalette"

const log = logger("ProgressMetrics")

export interface MetricExplanation {
  per_sentence_scores: {
    [label: string]: { idx: string | number; score: number }[]
  }
  candidate_labels: string[]
}

const AXIS_LABEL_FILL = "var(--coval-text-muted)"

export function ProgressMetrics({
  result_json,
  setSelectedMessage,
  xLabel,
  yLabel,
  label = "Score per message by label",
}: {
  result_json: MetricExplanation
  /** When set, clicking a point, or its table row's button, selects that message. */
  setSelectedMessage?: (message: number | null) => void
  xLabel?: string
  yLabel?: string
  /** The chart's accessible name. */
  label?: string
}) {
  const parsedExplanation = result_json
  if (
    !parsedExplanation.candidate_labels ||
    !parsedExplanation.per_sentence_scores
  ) {
    log.warn("Missing required fields in explanation", {
      hasLabels: !!parsedExplanation.candidate_labels,
      hasScores: !!parsedExplanation.per_sentence_scores,
    })
    return null
  }

  if (Object.keys(parsedExplanation).length === 0) {
    return null
  }

  const { candidate_labels, per_sentence_scores } = parsedExplanation

  // Distinct line color per label from the shared line palette (yellow excluded).
  const lineColors = lineSeriesColors(candidate_labels.length)

  // Merge each label's per-sentence scores into one row per message index so
  // all labels render as separate series in a single chart.
  const rowsByIdx = new Map<string, Record<string, number | string>>()
  for (const label of candidate_labels) {
    for (const point of per_sentence_scores[label] ?? []) {
      const key = String(point.idx)
      const row = rowsByIdx.get(key) ?? { idx: point.idx }
      row[label] = point.score
      rowsByIdx.set(key, row)
    }
  }
  const data = Array.from(rowsByIdx.values()).sort(
    (a, b) => Number(a.idx) - Number(b.idx),
  )

  const summary = summarizeLabels(candidate_labels, per_sentence_scores)
  const messageHeader = xLabel ?? "Message"
  const table: ChartTable = {
    columns: [
      { key: "idx", header: messageHeader },
      ...candidate_labels.map((candidate, index) => ({
        key: `label-${index}`,
        header: candidate,
        numeric: true,
      })),
      ...(setSelectedMessage ? [{ key: "select", header: "Select" }] : []),
    ],
    rows: data.map((row) => {
      const messageIndex = Number(row.idx)
      return {
        key: String(row.idx),
        cells: {
          idx: String(row.idx),
          ...Object.fromEntries(
            candidate_labels.map((candidate, index) => {
              const score = row[candidate]
              return [
                `label-${index}`,
                typeof score === "number" ? score.toFixed(3) : "—",
              ]
            }),
          ),
          select:
            setSelectedMessage && !Number.isNaN(messageIndex) ? (
              <Button
                type="button"
                variant="secondary"
                aria-label={`Show message ${row.idx}`}
                onClick={() => setSelectedMessage(messageIndex)}
              >
                Show
              </Button>
            ) : null,
        },
      }
    }),
  }

  return (
    <ChartFigure
      label={label}
      summary={summary}
      table={table}
      className="w-full h-full"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          role="img"
          aria-label={label}
          data={data}
          margin={{
            top: 8,
            right: 16,
            left: yLabel ? 8 : -12,
            bottom: xLabel ? 16 : 0,
          }}
          onClick={(event) => {
            if (!setSelectedMessage) {
              return
            }
            const rawIdx = (
              event.activePayload?.[0] as
                | { payload?: { idx?: string | number } }
                | undefined
            )?.payload?.idx

            if (rawIdx == null) {
              setSelectedMessage(null)
              return
            }

            const clickedIndex =
              typeof rawIdx === "number" ? rawIdx : Number(rawIdx)

            if (Number.isNaN(clickedIndex)) {
              setSelectedMessage(null)
              return
            }

            setSelectedMessage(clickedIndex)
          }}
        >
          <CartesianGrid
            stroke={DIVIDER_STROKE}
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="idx"
            tick={{ fontSize: 10 }}
            stroke={DIVIDER_STROKE}
            tickLine={false}
            tickMargin={8}
            label={
              xLabel
                ? {
                    value: xLabel,
                    position: "insideBottom",
                    offset: -8,
                    style: {
                      fontSize: 10,
                      fill: AXIS_LABEL_FILL,
                    },
                  }
                : undefined
            }
          />
          <YAxis
            domain={[0, 1]}
            width={40}
            tick={{ fontSize: 10 }}
            stroke={DIVIDER_STROKE}
            label={
              yLabel
                ? {
                    value: yLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: {
                      fontSize: 10,
                      fill: AXIS_LABEL_FILL,
                    },
                  }
                : undefined
            }
          />
          <Tooltip
            contentStyle={{ fontSize: 11 }}
            formatter={(value: number) => value.toFixed(3)}
            itemSorter={(item) => -Number(item.value)}
          />
          <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11 }} />
          {candidate_labels.map((label, labelIndex) => (
            <Line
              key={label}
              dataKey={label}
              name={label}
              type="monotone"
              stroke={lineColors[labelIndex % lineColors.length]}
              strokeWidth={1.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}

function summarizeLabels(
  candidateLabels: string[],
  scores: MetricExplanation["per_sentence_scores"],
): string | undefined {
  const averages = candidateLabels
    .map((candidate) => {
      const points = scores[candidate] ?? []
      if (points.length === 0) {
        return null
      }
      const mean =
        points.reduce((sum, point) => sum + point.score, 0) / points.length
      return { candidate, mean }
    })
    .filter((entry): entry is { candidate: string; mean: number } =>
      Boolean(entry),
    )
    .sort((a, b) => b.mean - a.mean)
  if (averages.length === 0) {
    return undefined
  }
  return `Average score by label: ${averages
    .map((entry) => `${entry.candidate} ${entry.mean.toFixed(2)}`)
    .join(", ")}.`
}
